/**
 * Which scenario *this tab* has open.
 *
 * It lives in the URL rather than in local storage, because local storage is
 * shared between tabs and the whole point is that two tabs can sit on two
 * different scenarios and be edited independently. A URL also survives a
 * reload, can be bookmarked, and can be handed to someone else.
 */
const PARAM = 'scenario';

export function pinnedScenarioId(): string | null {
  return new URLSearchParams(window.location.search).get(PARAM);
}

/** Record the open scenario in the address bar, without adding a history entry. */
export function pinScenario(scenarioId: string): void {
  const url = new URL(window.location.href);
  if (url.searchParams.get(PARAM) === scenarioId) return;
  url.searchParams.set(PARAM, scenarioId);
  window.history.replaceState(null, '', url);
}

/** The address for a scenario, for "open in a new tab". */
export function scenarioHref(scenarioId: string): string {
  const url = new URL(window.location.href);
  url.searchParams.set(PARAM, scenarioId);
  return `${url.pathname}${url.search}`;
}

/**
 * A file id Drive is asking us to open, if that is why this tab exists.
 *
 * Drive's "Open with" sends the browser to the app's URL with a `state`
 * parameter — URL-encoded JSON naming the file, the action and the account.
 * Only `open` is handled: `create` is Drive asking the app to make a new file
 * in a folder, which is not something this app does.
 *
 * Anything unparseable is ignored rather than reported. The parameter is
 * Drive's, not ours, and a malformed one is not something the person clicking
 * can do anything about.
 */
export function driveOpenFileId(): string | null {
  const raw = new URLSearchParams(window.location.search).get('state');
  if (!raw) return null;
  try {
    const state = JSON.parse(raw) as { action?: string; ids?: unknown };
    if (state.action !== 'open') return null;
    const ids: unknown[] = Array.isArray(state.ids) ? state.ids : [];
    const first = ids[0];
    return typeof first === 'string' && first ? first : null;
  } catch {
    return null;
  }
}

/** Drop Drive's parameter once it has been acted on, so a reload does not re-import. */
export function clearDriveState(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('state')) return;
  url.searchParams.delete('state');
  window.history.replaceState(null, '', url);
}
